import Quill from 'quill';
import React, { useEffect, useRef } from 'react';
import 'quill/dist/quill.snow.css';
import './quill-v2-list-fix.css';

interface RichTextEditorProps {
  id?: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

/**
 * Quill host that attaches directly to a DOM ref (no react-quill / findDOMNode).
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  id,
  value,
  onChange,
  placeholder,
  readOnly = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  const syncingRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.innerHTML = '';

    const quill = new Quill(container, {
      theme: 'snow',
      placeholder,
      readOnly,
      modules: readOnly ? { toolbar: false } : undefined,
    });

    quillRef.current = quill;

    quill.on('text-change', () => {
      if (syncingRef.current) {
        return;
      }
      onChangeRef.current?.(quill.root.innerHTML);
    });

    return () => {
      quillRef.current = null;
      container.innerHTML = '';
    };
  }, [placeholder, readOnly]);

  useEffect(() => {
    const quill = quillRef.current;
    const container = containerRef.current;
    if (!quill || !container) {
      return;
    }

    const current = quill.root.innerHTML;
    if (value !== current) {
      // Quill focuses the editor while pasting HTML. Mark the sync so parents
      // (e.g. JobForm section tabs) can ignore this programmatic focus.
      syncingRef.current = true;
      container.dataset.rteSyncing = 'true';
      const hadFocus = quill.hasFocus();
      quill.clipboard.dangerouslyPasteHTML(value || '');
      if (!hadFocus) {
        quill.blur();
      }
      // Clear after this turn so focus handlers from paste still see the flag.
      requestAnimationFrame(() => {
        delete container.dataset.rteSyncing;
        syncingRef.current = false;
      });
    }
  }, [value]);

  useEffect(() => {
    quillRef.current?.enable(!readOnly);
  }, [readOnly]);

  return (
    <div
      id={id}
      className={className ?? 'rich-text-editor'}
      ref={containerRef}
    />
  );
};

export default RichTextEditor;
